import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import { useRef, useState } from "react";
import {
  getDataStudioRecommendations,
  saveRecommendationAsNote,
} from "../services/dataStudio/dataStudioRecommendationsApi";
import type {
  DataStudioRecommendation,
  GetDataStudioRecommendationsResponse,
} from "../services/dataStudio/dataStudioRecommendations.types";

/** Shown on every action that is not available yet. */
export const RECOMMENDATIONS_COMING_SOON = "Coming soon";

const hashKey = (connectionId: string, assetId: string) => `${connectionId}/${assetId}`;

/**
 * Loads recommendations when the panel opens. The endpoint's `profile_hash`
 * changes only when the profile does, so the result is cached per asset and
 * hash and never refetched on its own. The hash is unknown until the first
 * answer, so it is remembered per asset for later opens.
 */
export const useDataStudioRecommendationsPanel = (connectionId: string, assetId: string) => {
  const queryClient = useQueryClient();
  const hashes = useRef<Record<string, string>>({});
  const [savedIds, setSavedIds] = useState<string[]>([]);

  const query = useQuery<GetDataStudioRecommendationsResponse>({
    queryKey: queryKeys.dataStudio.recommendations(connectionId, assetId, hashes.current[hashKey(connectionId, assetId)] ?? null),
    queryFn: async () => {
      const data = await getDataStudioRecommendations({ connectionId, assetId });
      hashes.current[hashKey(connectionId, assetId)] = data.profile_hash;
      queryClient.setQueryData(queryKeys.dataStudio.recommendations(connectionId, assetId, data.profile_hash), data);
      return data;
    },
    staleTime: Infinity,
    retry: false,
  });

  const save = useMutation({
    mutationFn: (item: DataStudioRecommendation) => saveRecommendationAsNote(connectionId, item),
    onSuccess: (_data, item) => {
      setSavedIds((ids) => (ids.includes(item.id) ? ids : [...ids, item.id]));
      void queryClient.invalidateQueries({ queryKey: queryKeys.dataStudio.tableProfileColumns(connectionId, assetId) });
    },
  });

  return {
    status: query.data?.status ?? null,
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    errorMessage: query.isError ? "Recommendations could not be loaded." : null,
    retry: () => void query.refetch(),
    comingSoonLabel: RECOMMENDATIONS_COMING_SOON,
    savedIds,
    savingId: save.isPending ? save.variables?.id ?? null : null,
    saveErrorId: save.isError ? save.variables?.id ?? null : null,
    saveAsNote: (item: DataStudioRecommendation) => save.mutate(item),
  };
};
